import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import ChrisPhoto from "../candidates/Louisk.jpg";



export default function HeadOfPartnershipsLouisK() {
const candidateProfileCopy = {
  name: "Louis",
  location: "Austin, TX",
  compensation: {
    range: "$130K+ base, flexible on structure",
  },
  availability: "Available within one week of offer",

  working_style:
    "Louis came up the hard way, starting as a cold calling SDR and working his way to enterprise accounts and then to building a sales team of his own. He leads by doing, still picking up the phone himself so his reps know he is in it with them, and he sells by listening first rather than pushing. He is a builder at heart who likes turning a messy, manual process into something repeatable, and he has spent his whole career inside healthcare working with providers and clinics.",

  loom_video_link: "https://fathom.video/share/qsv8A998haHy3za8BpXRiqFVw1q2xix2",

  tools: [
    {
      category: "Partnership & Account Growth",
      items: [
        "Growing provider and clinic accounts from a handful to thousands",
        "Reactivating and expanding dormant partnerships",
        "Working up to C suite decision makers to win buy in",
      ],
    },
    {
      category: "Building & Scaling",
      items: [
        "Built SDR and sales teams from scratch",
        "Turned manual processes into repeatable systems",
        "Grew new patient enrollments from 15 to 500 a month",
      ],
    },
    {
      category: "SDR Leadership",
      items: [
        "Hands on coaching, still on the phones with the team",
        "Clear goals, growth paths, and accountability",
        "Training brand new reps into confident closers",
      ],
    },
    {
      category: "Healthcare & Compliance",
      items: [
        "10 plus years selling into healthcare providers",
        "Comfortable and careful with HIPAA and patient data",
        "Experience with OBGYN, dental, optometry, and behavioral health",
      ],
    },
  ],

  soft_skills: [
    "10 plus years in healthcare sales and account growth",
    "Built SDR and sales teams from the ground up",
    "Hands on leader who still works the phones with his reps",
    "Listens first and builds long term provider relationships",
  ],

  about_candidate:
    "Louis has spent more than a decade in healthcare sales, growing from an SDR at ZocDoc to managing its largest enterprise accounts, where he reactivated a dormant national optometry brand and expanded it from around 100 locations to more than 2,000. For the last two years he has been the first sales hire at a small remote care company, where he built the SDR and account management teams from scratch and grew new patient enrollments from about 15 a month to 500. He is hands on, hungry, and comfortable building a revenue engine from nothing, and he has worked with OBGYN and other provider types throughout his career.",

  core_strengths: [
    {
      headline: "Built a revenue engine from nothing",
      description:
        "As the first sales hire at a small remote care company, Louis built the SDR and account management teams from scratch and grew new patient enrollments from about 15 a month to 500. Standing up a partnership channel from a blank page is exactly the kind of work he has already done.",
    },
    {
      headline: "Reactivates and grows partner accounts",
      description:
        "At ZocDoc he took a dormant national optometry brand that did not even like the product, worked his way up to the CEO and COO, and expanded it from around 100 locations to more than 2,000. He is good at turning a cold or stalled relationship into real, growing volume.",
    },
    {
      headline: "A hands on SDR leader",
      description:
        "Managing reps is his favorite part of the job, and he still cold calls every day so his team knows he is in it with them. He sets clear goals and growth paths, coaches relentlessly, and has trained brand new people into confident closers.",
    },
    {
      headline: "Wins on listening, not pushing",
      description:
        "His approach is to learn the space, sit down, and really listen to a provider's problems before ever pitching. That patient, trust first style suits fertility and women's health, where relationships are everything.",
    },
  ],

  relevant_experience: [
    {
      headline: "Sales and Customer Success Director | WellBe Health (Remote Care) | 2024 to Present",
      bullets: [
        "First sales hire, built the SDR and account management teams from scratch",
        "Grew new patient enrollments from about 15 a month to 500",
        "Built the processes, training, and metrics, reaching profitability in two months",
      ],
    },
    {
      headline: "Enterprise Account Manager | ZocDoc | 2022 to 2024",
      bullets: [
        "Managed major hospital, dental, and optometry accounts covering thousands of providers",
        "Reactivated and expanded a national optometry brand from around 100 to 2,000 plus locations",
        "Top performer, contributing to annual revenue above $100M",
      ],
    },
    {
      headline: "Strategic Account Manager | ZocDoc | 2015 to 2021",
      bullets: [
        "Earned President's Club status every year by exceeding sales targets",
        "Grew existing accounts, reduced churn, and deepened provider relationships",
        "Improved the sales process and tools alongside leadership",
      ],
    },
    {
      headline: "Senior Account Representative and Sales Manager | Merchant Industry | 2008 to 2014",
      bullets: [
        "Built a client portfolio of more than 480 accounts",
        "Hired, trained, and coached new reps into closers",
        "Built and developed an SDR team from the ground up",
      ],
    },
  ],
};

const technicalAssessment = {
  meta: {
    role: "Head of Healthcare Partnerships",
    interviewDate: "June 5, 2026",
    analyst: "AI Architechs Senior Hiring Analysis",
  },

  snapshot:
    "Louis is a hands on builder who has grown provider accounts from a handful to thousands and stood up a sales engine from scratch as a company's first sales hire. He came up from cold calling SDR to enterprise accounts to leadership, so he knows the work at every level and still does it himself. His sweet spot is building the SDR engine and winning provider relationships through listening rather than pressure. The things to confirm are that he can own partnership strategy at a Head level, since his path has been more builder and closer than senior executive, and that he can ramp into fertility, since his women's health exposure is through OBGYN rather than fertility clinics. His pay expectations are modest and flexible.",

  scorecard: [
    {
      dimension: "Partnership and Referral Channel Development",
      score: 8,
      rationale:
        "His best story is reactivating a dormant national optometry brand and expanding it from around 100 to more than 2,000 locations by working up to the CEO and COO. Across his career he has grown provider and clinic accounts at scale, which is real channel building, leaning a little more toward growing accounts than originating brand new partnerships.",
    },
    {
      dimension: "Building From Zero and Pipeline Construction",
      score: 8,
      rationale:
        "As the first sales hire at a remote care company he built the SDR and account management teams from scratch, created the processes and metrics, and grew enrollments from about 15 a month to 500. Strong, proven build from zero experience, with his answer for approaching a brand new vertical leaning more on learning and listening than a detailed target and outreach plan.",
    },
    {
      dimension: "Revenue and Commercial Thinking",
      score: 7,
      rationale:
        "He thinks about value on both sides, checks in regularly on whether partners are seeing results, and prioritizes the accounts with the most room to grow. His framing leans toward relationship value more than hard revenue math, though his track record includes contributing to over $100M in revenue.",
    },
    {
      dimension: "Partner Conversion and Lifecycle Management",
      score: 8,
      rationale:
        "Strong and concrete. He always books the next call before ending the current one, makes sure every decision maker is in the room, and stays close after the deal with regular check ins so the relationship never goes dormant. He aims to be an advisor, not just a vendor.",
    },
    {
      dimension: "SDR and Sales Team Leadership",
      score: 9,
      rationale:
        "This is his standout. He has built SDR teams from scratch more than once, still cold calls daily so his reps see he is in it with them, and leads with clear goals, growth paths, and accountability. Genuine, hands on leadership with a real coaching philosophy.",
    },
    {
      dimension: "Regulated Healthcare Experience and Compliance",
      score: 8,
      rationale:
        "More than a decade selling into healthcare, fully used to HIPAA and the care needed around patient data, with experience across OBGYN, dental, optometry, and behavioral health. Comfortable in the regulated world, with less depth on the specific partnership and kickback rules than some.",
    },
    {
      dimension: "Fertility and Women's Health Vertical Exposure",
      score: 6,
      rationale:
        "He has sold into OBGYN practices and a range of other providers, so women's health is not unfamiliar, but he has not worked with fertility clinics specifically and said as much. A short ramp on that exact vertical.",
    },
    {
      dimension: "Ambition and Drive",
      score: 8,
      rationale:
        "Real hunger. He rose from the bottom as a cold calling SDR, built a sales division from nothing, and still works the phones himself. His pay expectations are modest and flexible, a clear sign he wants the opportunity more than the package.",
    },
  ],

  strengths: [
    {
      headline: "Has built the engine before",
      description:
        "Louis has stood up an SDR and sales team from scratch and grown enrollments more than thirty times over, so building a partnership channel from nothing is familiar ground.",
    },
    {
      headline: "Turns cold into growing",
      description:
        "His reactivation and expansion of a dormant national brand from 100 to 2,000 locations shows he can warm up a hard relationship and grow it into real volume.",
    },
    {
      headline: "A leader who builds reps",
      description:
        "He coaches hands on, still on the phones himself, and has a real knack for turning brand new people into confident closers, which matters for the SDR side of this role.",
    },
    {
      headline: "Hungry and low ego",
      description:
        "He came up from the bottom, still does the daily work, and is flexible on pay because he wants the opportunity, which is the kind of drive this build calls for.",
    },
  ],

  flags: [
    {
      headline: "Builder and closer more than senior strategist",
      description:
        "Louis has grown from SDR to building a sales division, but he is earlier in the senior leadership arc than a seasoned Head of Partnerships. Worth confirming he can own the full partnership strategy at this level, not only build and close.",
    },
    {
      headline: "Fertility is new ground",
      description:
        "He has sold into OBGYN and many other providers, but not fertility clinics specifically. A short ramp on that exact vertical.",
    },
    {
      headline: "Relationship led more than numbers led",
      description:
        "He judges partnerships more by engagement and value than by hard revenue math, even though he has built metrics before. Worth confirming he will bring the numbers discipline a large revenue target needs.",
    },
  ],

  when_to_hire: [
    "You want a hands on builder who has stood up a sales engine from scratch and is comfortable starting from nothing.",
    "Strong SDR leadership matters to you, and you want someone who coaches by example and still works the phones.",
    "You value someone hungry and low ego who came up the hard way and wants the opportunity, not just the title.",
    "You like a listen first, trust building style that fits how fertility and women's health providers tend to buy.",
  ],

  when_to_be_cautious: [
    "You want a seasoned senior executive who has run partnership strategy at a high level, since his path has been more builder and closer.",
    "You need someone who already knows fertility clinics and can start those relationships immediately.",
    "You want a numbers first strategist, since his instinct leans toward relationships and engagement over hard pipeline math.",
  ],

  bottom_line:
    "Louis brings real hunger, a proven ability to build a sales engine from scratch, and a hands on leadership style that fits the SDR side of this role, all inside a healthcare career working with providers. The things to confirm are whether he can own partnership strategy at a Head level and how quickly he can ramp into fertility, since his women's health exposure is through OBGYN. For building the team and the channel from the ground up, he is well suited.",
};

  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar placeholder */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <div className="relative w-36 h-36 rounded-full border-4 border-[#36D1FF] shadow-lg overflow-hidden">
              <img src={ChrisPhoto} alt="Chris Weston" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Watch Interview Button */}
          <a
            href={candidateProfileCopy.loom_video_link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-6 flex items-center justify-center gap-2 bg-gradient-to-r from-[#36D1FF] to-[#0099CC] hover:from-[#5DDDFF] hover:to-[#00AADD] text-[#01014C] font-bold text-sm py-3 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-[#36D1FF]/20 hover:shadow-[#36D1FF]/40"
          >
            ▶ Watch Interview
          </a>

          {/* Tools */}
          <div className="w-full mt-4 mb-6 text-left">
            <h3 className="text-sm font-semibold mb-3 text-[#B3E9FF] uppercase">
              Tools & Capabilities
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-4">
                <p className="text-xs font-semibold text-[#36D1FF] mb-1 uppercase tracking-wide">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((tool, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-xs text-[#E0F7FF]/90 leading-snug"
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#36D1FF] shrink-0" />
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Key Profile Highlights
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo placeholder */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI Architechs" className="h-16 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div key={i} className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div key={i} className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">{row.dimension}</td>
                        <td className="px-4 py-3 align-top"><ScorePill score={row.score} /></td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">{row.rationale}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Cautions */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div key={i} className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}